/**
 * Гостевая корзина. У вошедшего корзина серверная, и правда одна — там;
 * гостю же хранить её негде, кроме браузера, поэтому здесь только количества:
 * названия и цены всё равно берутся из каталога, а не из памяти браузера.
 *
 * Ключ — идентификатор товара. Вариаций у товара в API нет, поэтому и строк
 * «один товар в двух дозировках» не бывает.
 */
export type CartMap = Readonly<Record<string, number>>

/** Ноль и меньше убирает строку целиком. */
export function setQuantity(cart: CartMap, productId: number, quantity: number): CartMap {
  const next = { ...cart }
  if (quantity <= 0) delete next[String(productId)]
  else next[String(productId)] = Math.min(99, quantity)
  return next
}

export function changeQuantity(cart: CartMap, productId: number, delta: number): CartMap {
  return setQuantity(cart, productId, (cart[String(productId)] ?? 0) + delta)
}

export function quantityOf(cart: CartMap, productId: number): number {
  return cart[String(productId)] ?? 0
}

export function cartCount(cart: CartMap): number {
  return Object.values(cart).reduce((sum, quantity) => sum + quantity, 0)
}

export interface GuestLine {
  productId: number
  qty: number
}

/** Гостевая корзина в том виде, в каком её принимает `POST /cart/merge`. */
export function guestLines(cart: CartMap): GuestLine[] {
  return Object.entries(cart)
    .map(([id, qty]) => ({ productId: Number(id), qty }))
    .filter((line) => line.productId > 0 && line.qty > 0)
}

/** Что мешает оформить заказ: сервер называет причину, экран — ключ перевода. */
export function issueKey(issue: 'unavailable' | 'out_of_stock'): string {
  return issue === 'unavailable' ? 'cart:issues.removed' : 'cart:issues.outOfStock'
}

/**
 * Порядок строк, который не прыгает. Сервер ставит изменённую строку в конец,
 * и в корзине она уезжала бы из-под пальца после каждого «+». Держим порядок
 * первого появления: новые товары — в конец, остальные остаются на месте.
 * Возвращает строки и дополненный порядок.
 */
export function keepOrder<T extends { productId: number }>(
  lines: T[],
  order: number[],
): [T[], number[]] {
  const next = [...order, ...lines.map((l) => l.productId).filter((id) => !order.includes(id))]
  const sorted = [...lines].sort((a, b) => next.indexOf(a.productId) - next.indexOf(b.productId))
  return [sorted, next]
}
